package de.oevbs.kunde.kundenportal2.services.authentication

import de.oevbs.kosmos.ui.elements.button.btn
import de.oevbs.kosmos.ui.forms.inputgroup.inputDate
import de.oevbs.kosmos.ui.forms.inputgroup.inputText
import de.oevbs.kosmos.ui.forms.radiogroup.radioGroup
import de.oevbs.kosmos.ui.icons.MaterialIconsOutlined
import de.oevbs.kunde.kundenportal2.GeneralProgressStep
import de.oevbs.kunde.kundenportal2.PinAuthenticationStep
import de.oevbs.kunde.kundenportal2.components.*
import de.oevbs.kunde.kundenportal2.model.services.authentication.*
import de.oevbs.kunde.kundenportal2.services.authentication.store.PartnerAuthenticationStore
import de.oevbs.kunde.kundenportal2.services.authentication.store.PinAuthenticationStore
import dev.fritz2.core.RenderContext
import dev.fritz2.core.invoke
import dev.fritz2.headless.validation.Severity

fun RenderContext.partnerAuthenticationStart() {
    headerImageLayout {
        title(PartnerAuthenticationStore.header)
        content {
            renderPartnerAuthentication()
        }
    }
}

fun RenderContext.pinAuthenticationStart() {
    val pinAuthStore = PinAuthenticationStore(job)
    headerImageLayout {
        title(pinAuthStore.header)
        content {
            renderPinAuthentication(pinAuthStore)
        }
    }
}

fun RenderContext.renderPartnerAuthentication() {

    PartnerAuthenticationStore.currentStep.render { step ->

        GlobalServiceMessagesStore.clear()

        when (step) {
            GeneralProgressStep.SUBMIT1 -> partnerAuthenticationInputData()
            GeneralProgressStep.SUCCESS2 -> messagePageText(
                header = "Ihr Freischaltcode ist auf dem Weg zu Ihnen",
                text = "Damit Sie alle Funktionen von Meine Öffentliche nutzen können, benötigen Sie Ihren persönlichen Freischaltcode. Diesen erhalten Sie in den nächsten Tagen per Post von uns."
            )

            else -> console.error("Unbekannter ProgressStep bei PartnerAuthentication: '${step}'.")
        }
    }
}

fun RenderContext.renderPinAuthentication(pinAuthStore: PinAuthenticationStore) {

    pinAuthStore.currentStep.render { step ->

        GlobalServiceMessagesStore.clear()

        when (step) {
            PinAuthenticationStep.CHECKPIN1 -> pinAuthenticationInputPin(pinAuthStore)
            PinAuthenticationStep.SELECTMAIL2 -> pinAuthenticationSelectMail(pinAuthStore)
            PinAuthenticationStep.SUCCESS3 -> messagePageText(
                header = "Erfolgreich freigeschaltet!",
                text = "Sollten Sie nicht automatisch zu Ihren Daten weitergeleitet werden, melden Sie sich bitte ab und loggen sich erneut ein."
            )

            else -> console.error("Unbekannter ProgressStep bei PinAuthentication: '${step}'.")
        }
    }
}

private fun RenderContext.partnerAuthenticationInputData() {

    paragraph {
        +"Um alle Funktionen von Meine Öffentliche"
        +" nutzen zu können, müssen Sie Ihre Vertragsdaten mit Ihrem Account verknüpfen. Dazu müssen wir Sie eindeutig identifizieren."
        b { +" Bitte stellen Sie sicher, dass Ihre aktuelle Anschrift bei uns hinterlegt ist - haben Sie uns über Ihren letzten Umzug informiert? Andernfalls melden Sie sich bitte telefonisch bei uns." }
        + " Geben Sie dann die folgenden Daten ein, damit wir Ihnen einen Brief mit einem Freischaltcode zusenden können."

        br {}
        +" Schauen Sie auf ein aktuelles "
        inlineLink(
            text = "Schreiben von uns",
            link = "https://www.oeffentliche.de/export/sites/oevbs/_resources/downloads/pdf/service/partnernummer-musterbrief.pdf",
            target = "_blank"
        )
        +", um die Nummer eines laufenden Vertrages und Ihre Partnernummer zu finden. Zusätzlich benötigen wir Ihr Geburtsdatum."
        +" Der Freischaltcode wird Ihnen dann in den nächsten Tagen an Ihre bei uns hinterlegte Adresse zugestellt."
    }

    inputDate {
        label("Geburtsdatum")
        value(PartnerAuthenticationStore.map(PartnerAuthentication.dateOfBirth()))
    }

    inputText {
        label("Aktive Vertragsnummer (nicht Policennummer)")
        value(PartnerAuthenticationStore.map(PartnerAuthentication.contractNo()))
        iconClickableWithPopover(MaterialIconsOutlined.help, HelpPopovers.policyId)
    }

    inputText {
        label("Partnernummer")
        value(PartnerAuthenticationStore.map(PartnerAuthentication.partnerId()))
        iconClickableWithPopover(MaterialIconsOutlined.help, HelpPopovers.partnerId)
    }

    buttonContainer {
        btn {
            label("Freischaltcode anfordern")
            busy(globalSubmitTracker.data)
        }.clicks handledBy PartnerAuthenticationStore.submitAuthentication
    }
}

private fun RenderContext.pinAuthenticationInputPin(pinAuthStore: PinAuthenticationStore) {

    paragraph {
        +"Bitte geben Sie den 6-stelligen Freischaltcode ein, den wir Ihnen per Post zugeschickt haben."
    }

    inputCode(pinAuthStore.map(PinAuthentication.pin()))

    paragraph {
        b { +"Brief nicht erhalten?" }
        br {}
        +"Die Zustellung kann bis zu 5 Werktage dauern. Sollten Sie den Brief dennoch nicht erhalten haben, können Sie "
        textualButton("hier einen neuen Freischaltcode anfordern") handledBy pinAuthStore.requestNewPin
        +"."
    }

    buttonContainer {
        btn {
            label("Verträge freischalten")
            busy(globalSubmitTracker.data)
        }.clicks handledBy pinAuthStore.submitPin
    }
}

private fun RenderContext.pinAuthenticationSelectMail(pinAuthStore: PinAuthenticationStore) {

    val crmMailSub = pinAuthStore.map(PinAuthentication.crmMail())
    val accountMailSub = pinAuthStore.map(PinAuthentication.accountMail())
    val selectedMailSub = pinAuthStore.map(PinAuthentication.selectedMail())

    selectedMailSub.update(crmMailSub.current)

    messagePageRender(header = "Fast geschafft!", severity = Severity.Info) {

        h4 { +"Wählen Sie noch eine Ihrer Mail-Adressen aus:" }

        paragraph {
            +"Die Mail-Adresse "
            span { b { crmMailSub.data.renderText() } }
            +" in Ihren Vertragsdaten weicht von der Adresse "
            span { b { accountMailSub.data.renderText() } }
            +" in Ihrem Account ab. Bitte entscheiden Sie, welche Adresse ab sofort für beide Zwecke verwendet werden soll. Wenn Sie keine Auswahl treffen, verwenden wir die Adresse aus Ihren Vertragsdaten."
        }

        radioGroup {
            value(selectedMailSub)
            options = listOf(accountMailSub.current, crmMailSub.current)
        }

        buttonContainer {
            btn {
                label("Mail speichern")
                busy(globalSubmitTracker.data)
            }.clicks handledBy pinAuthStore.submitMailSelect
        }
    }
}